import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Stack, Fade } from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";

// Import your hero images here
import heroImage1 from "../images/speciescollage.png";
import heroImage2 from "../images/snowleopard.png";
import heroImage3 from "../images/leopard.png";
import heroImage4 from "../images/deer.png";
import heroImage5 from "../images/markhor.png";
import heroImage6 from "../images/vehicleperson.png";
import heroImage7 from "../images/fox.png";

const slides = [
  {
    image: heroImage1,
    title: "Early Warning System",
    subtitle: "Advanced wildlife monitoring and conservation platform",
    cta: "Start Monitoring",
  },
  {
    image: heroImage2,
    title: "Snow Leopard Detection",
    subtitle: "AI-powered real-time species identification and tracking",
    cta: "View Species",
  },
  {
    image: heroImage3,
    title: "Leopard Monitoring",
    subtitle: "Protect and track leopard populations with advanced AI",
    cta: "Get Started",
  },
  {
    image: heroImage4,
    title: "Deer Population Tracking",
    subtitle: "Monitor deer movements and habitat patterns",
    cta: "Learn More",
  },
  {
    image: heroImage5,
    title: "Markhor Conservation",
    subtitle: "Protect the majestic Markhor with real-time monitoring",
    cta: "Get Started",
  },
  {
    image: heroImage6,
    title: "Vehicle & Person Detection",
    subtitle: "Monitor human-wildlife interactions and threats",
    cta: "View Dashboard",
  },
  {
    image: heroImage7,
    title: "Fox Detection & Tracking",
    subtitle: "Track fox populations and movement patterns",
    cta: "Get Started",
  },
];

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const currentSlideData = slides[currentSlide];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100vh",
        minHeight: 600,
        maxHeight: 700,
        overflow: "hidden",
        background: "#0a0a0a",
      }}
    >
      {/* Animated Particle Effect */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {[...Array(50)].map((_, i) => {
          const size = 2 + Math.random() * 6;
          const duration = 8 + Math.random() * 15;
          const delay = Math.random() * 10;
          const opacity = 0.1 + Math.random() * 0.4;
          const colors = [
            `rgba(76, 175, 80, ${opacity})`,
            `rgba(33, 150, 243, ${opacity})`,
            `rgba(255, 152, 0, ${opacity})`,
            `rgba(255, 255, 255, ${opacity * 0.5})`,
            `rgba(76, 175, 80, ${opacity * 0.6})`,
          ];
          const color = colors[Math.floor(Math.random() * colors.length)];

          return (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: size,
                height: size,
                borderRadius: Math.random() > 0.5 ? "50%" : "2px",
                background: color,
                boxShadow: `
                  0 0 ${size * 2}px ${color},
                  0 0 ${size * 4}px ${color}40
                `,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `
                  floatParticle ${duration}s ease-in-out infinite,
                  glowPulse ${3 + Math.random() * 4}s ease-in-out infinite alternate
                `,
                animationDelay: `${delay}s`,
                transform: `scale(${0.5 + Math.random()})`,
                filter: `blur(${Math.random() > 0.7 ? 1 : 0}px)`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -size * 2,
                  left: -size * 2,
                  right: -size * 2,
                  bottom: -size * 2,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
                  animation: `glowRing ${4 + Math.random() * 6}s ease-in-out infinite alternate`,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  width: size * 3,
                  height: size * 3,
                  top: -size,
                  left: -size,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
                  animation: `pulseRing ${3 + Math.random() * 5}s ease-in-out infinite alternate`,
                },
              }}
            />
          );
        })}
      </Box>

      {/* Orbital Particles */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        {[...Array(8)].map((_, i) => {
          const angle = (i / 8) * Math.PI * 2;
          const radius = 150 + Math.random() * 200;
          const duration = 20 + Math.random() * 15;
          const size = 3 + Math.random() * 5;
          const opacity = 0.1 + Math.random() * 0.3;
          const colors = [
            `rgba(76, 175, 80, ${opacity})`,
            `rgba(33, 150, 243, ${opacity})`,
            `rgba(255, 152, 0, ${opacity})`,
            `rgba(255, 107, 107, ${opacity})`,
          ];
          const color = colors[i % colors.length];

          return (
            <Box
              key={`orbit-${i}`}
              sx={{
                position: "absolute",
                width: size,
                height: size,
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 ${size * 3}px ${color}`,
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) rotate(${angle}rad) translateX(${radius}px)`,
                animation: `orbital ${duration}s linear infinite`,
                animationDelay: `${i * 0.5}s`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  width: size * 4,
                  height: size * 4,
                  top: -size * 1.5,
                  left: -size * 1.5,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${color}30 0%, transparent 70%)`,
                },
              }}
            />
          );
        })}
      </Box>

      {/* Slideshow Images */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {slides.map((slide, index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              top: -150,
              left: 0,
              right: 0,
              bottom: 80,
              opacity: currentSlide === index ? 1 : 0,
              background: `
                url(${slide.image}) center/cover no-repeat
              `,
              transform: currentSlide === index ? "scale(0.4)" : "scale(0.6)",
              transition: "all 0.8s ease",
            }}
          />
        ))}
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          px: { xs: 2, md: 6 },
        }}
      >
        <Box
          sx={{
            maxWidth: 900,
            mx: "auto",
            textAlign: "center",
          }}
        >
          <Fade in timeout={500}>
            <Box>
              <Typography
                variant="h1"
                fontWeight={800}
                sx={{
                  fontSize: { xs: "2.5rem", md: "3rem" },
                  mt: 40,
                  mb: 2,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, rgba(218, 214, 214, 0.95) 100%)",
                  backgroundSize: "200% 200%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shine 4s ease-in-out infinite",
                  textShadow: "0 4px 30px rgba(0,0,0,0.3)",
                }}
              >
                {currentSlideData.title}
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: "rgba(255,255,255,0.8)",
                  fontWeight: 300,
                  fontSize: { xs: "1rem", md: "1.5rem" },
                  maxWidth: 700,
                  mx: "auto",
                }}
              >
                {currentSlideData.subtitle}
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background:
                      "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
                    px: 5,
                    py: 1.5,
                    borderRadius: 3,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 30px rgba(76,175,80,0.4)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 40px rgba(76,175,80,0.6)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  href="/dashboard"
                >
                  {currentSlideData.cta}
                </Button>
              </Stack>

              {/* Slide Indicators */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  mt: 5,
                }}
              >
                {slides.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: currentSlide === index ? 40 : 10,
                      height: 8,
                      borderRadius: 2,
                      background:
                        currentSlide === index
                          ? "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)"
                          : "rgba(161, 159, 159, 0.79)",
                      transition: "all 0.5s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>

      {/* CSS Animations */}
      <style jsx="true">{`
        @keyframes floatParticle {
          0% {
            transform: translate(0, 0) scale(1) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          50% {
            transform: translate(50px, -50px) scale(1.5) rotate(180deg);
            opacity: 0.8;
          }
          90% {
            opacity: 0.4;
          }
          100% {
            transform: translate(75px, 25px) scale(0.5) rotate(360deg);
            opacity: 0;
          }
        }
        @keyframes orbital {
          0% {
            transform: translate(-50%, -50%) rotate(0deg) translateX(150px);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg) translateX(150px);
          }
        }
        @keyframes glowPulse {
          0% {
            opacity: 0.2;
            transform: scale(1);
          }
          100% {
            opacity: 0.8;
            transform: scale(1.5);
            box-shadow: 0 0 20px rgba(76, 175, 80, 0.4);
          }
        }
        @keyframes glowRing {
          0% {
            transform: scale(1);
            opacity: 0;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        @keyframes pulseRing {
          0% {
            transform: scale(1);
            opacity: 0;
          }
          50% {
            opacity: 0.3;
          }
          100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        @keyframes shine {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </Box>
  );
}
